import React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { FiCalendar } from 'react-icons/fi';
import { TRACK_OPTIONS, GRADE_OPTIONS, CAMPUS_OPTIONS, DATE_FLOOR } from './constants';

interface DateRange {
  startDate: string;
  endDate: string;
}

interface FiltersState {
  dateRange: DateRange;
  campus: string[];
  grade: number[];
  track: string[];
}

interface FiltersSectionProps {
  filters: FiltersState;
  onChange: (filters: FiltersState) => void;
  dateErrors: { startDate?: string; endDate?: string };
}

const GRADE_OPTION_LIST = GRADE_OPTIONS.map((g) => g);
const TRACK_OPTION_LIST = TRACK_OPTIONS.map((t) => t);

const FiltersSection: React.FC<FiltersSectionProps> = ({ filters, onChange, dateErrors }) => {
  const today = new Date().toISOString().split('T')[0];
  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    onChange({
      ...filters,
      dateRange: { ...filters.dateRange, [field]: value },
    });
  };

  const handleGradeChange = (
    _event: React.SyntheticEvent,
    value: readonly { value: number; label: string }[]
  ) => {
    onChange({ ...filters, grade: value.map((g) => g.value) });
  };

  const handleTrackChange = (
    _event: React.SyntheticEvent,
    value: readonly { id: string; label: string }[]
  ) => {
    onChange({ ...filters, track: value.map((t) => t.id) });
  };

  const handleCampusChange = (
    _event: React.SyntheticEvent,
    value: readonly string[]
  ) => {
    onChange({ ...filters, campus: [...value] });
  };

  const selectedGradeObjects = GRADE_OPTION_LIST.filter((g) =>
    filters.grade.includes(g.value)
  );

  const selectedTrackObjects = TRACK_OPTION_LIST.filter((t) =>
    filters.track.includes(t.id)
  );

  return (
    <div className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
      <h3 className="text-lg font-medium text-gray-800 mb-1">Filters</h3>
      <p className="text-sm text-gray-500 mb-4">
        Date range is required. Campus, grade, and track filters are optional.
      </p>

      <div className="space-y-5">
        {/* Date Range */}
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">Date Range (required)</p>
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col">
              <label htmlFor="qb-start-date" className="text-sm font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="qb-start-date"
                  value={filters.dateRange.startDate}
                  min={DATE_FLOOR}
                  max={filters.dateRange.endDate || today}
                  onChange={(e) => handleDateChange('startDate', e.target.value)}
                  className={`p-2 pl-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    dateErrors.startDate ? 'border-red-400' : 'border-gray-300'
                  }`}
                />
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
              </div>
              {dateErrors.startDate && (
                <p className="text-xs text-red-500 mt-1">{dateErrors.startDate}</p>
              )}
            </div>

            <div className="flex flex-col">
              <label htmlFor="qb-end-date" className="text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  id="qb-end-date"
                  value={filters.dateRange.endDate}
                  min={filters.dateRange.startDate || DATE_FLOOR}
                  max={today}
                  onChange={(e) => handleDateChange('endDate', e.target.value)}
                  className={`p-2 pl-10 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    dateErrors.endDate ? 'border-red-400' : 'border-gray-300'
                  }`}
                />
                <FiCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
              </div>
              {dateErrors.endDate && (
                <p className="text-xs text-red-500 mt-1">{dateErrors.endDate}</p>
              )}
            </div>
          </div>
        </div>

        {/* Campus */}
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">Campus (optional)</p>
          <Autocomplete
            multiple
            options={CAMPUS_OPTIONS}
            value={filters.campus}
            onChange={handleCampusChange}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={filters.campus.length === 0 ? 'All campuses' : ''}
                size="small"
                sx={{ backgroundColor: 'white' }}
              />
            )}
          />
        </div>

        {/* Knowledge Grade */}
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">Knowledge Grade (optional)</p>
          <Autocomplete
            multiple
            options={GRADE_OPTION_LIST}
            value={selectedGradeObjects}
            getOptionLabel={(option) => option.label}
            onChange={handleGradeChange}
            isOptionEqualToValue={(option, val) => option.value === val.value}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedGradeObjects.length === 0 ? 'All grades' : ''}
                size="small"
                sx={{ backgroundColor: 'white' }}
              />
            )}
            size="small"
          />
        </div>

        {/* Track */}
        <div>
          <p className="text-sm font-semibold text-gray-700 mb-2">Track (optional)</p>
          <Autocomplete
            multiple
            options={TRACK_OPTION_LIST}
            value={selectedTrackObjects}
            getOptionLabel={(option) => option.label}
            onChange={handleTrackChange}
            isOptionEqualToValue={(option, val) => option.id === val.id}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedTrackObjects.length === 0 ? 'All tracks' : ''}
                size="small"
                sx={{ backgroundColor: 'white' }}
              />
            )}
            size="small"
          />
        </div>
      </div>
    </div>
  );
};

export default FiltersSection;
